import styled, { css } from 'styled-components/macro';
import { bool, func, number, oneOf, arrayOf } from 'prop-types';
import first from 'lodash.first';

import BaseTable from 'components/data-visualization/Table/Table';
import TableBody from 'components/data-visualization/Table/Body';
import TableHead from 'components/data-visualization/Table/DropdownHead';
import { SortingControls } from 'components/data-visualization/Table/DropdownHead.style';
import ContentLoader from 'components/utility/ContentLoader/ContentLoader';
import { TableRow, TableCell } from '../shared/TerritoryTable.style';
import TerritoryTableRow from '../TableRow/TableRow';
import ExpandableTableRow from '../ExpandedTableRow/ExpandedTableRow';
import CustomizedTableRow from '../TableRow/CustomizedTableRow';
import MobileTableRow from '../TableRow/MobileTableRow';

import {
  countries as countriesPropType,
  metric as metricPropType,
  salesTypes as salesTypesPropType,
} from 'propTypes';

const StyledTable = styled(BaseTable)`
  th:nth-of-type(2) ${SortingControls} {
    justify-content: flex-start;
  }

  ${({ isStreaming, hasOneHeader }) =>
    isStreaming &&
    css`
      th {
        padding: 0;
        ${hasOneHeader && 'width: 100%;'}

        button {
          text-align: right;
          padding: 0px;
        }
      }
    `}
`;

const withChangeMetricStreaming = new Set([
  'twtdOfGlobal',
  'lw',
  'twtd',
  'lwtd',
  'llw',
  'wowInProgress',
  'wowLastCompleted',
  'rtd',
]);
const withChangeMetric = new Set(['twtdOfGlobal', 'twtd', 'lw']);
const hideVisually = new Set(['expand', 'countryRank']);
const notSortable = new Set(['countryName']);

/**
 * (Territory) Table sets the column headers and renders the passed data in rows
 *
 * @param  {String} props.sortOrder ascending or descending
 * @param  {Function} props.setSortOrder set the sort order
 * @param  {Function} props.setSortMetric set the sort metric
 * @param  {String} props.selectedSalesType type of sale (e.g 'Album Equivalents')
 * @param  {Number} props.rowLimit amount of rows to display
 * @param  {Object} props.valueRange min and max numbers for the selected metric
 * @param  {Object} props.sortMetric metric to sort by
 * @param  {Array} props.territories territories to show
 * @param  {Array} props.tableHeaders keys to sort by
 * @param  {Boolean} props.isLargeScreen true if screen matches min-width
 * @param  {Boolean} props.withExandableRows artist and project get expandable rows
 * @param  {Function} props.changeSortMetric function to switch sorting metric
 */
function Table(props) {
  const {
    territories,
    tableHeaders,
    sortOrder,
    sortMetric,
    setSortOrder,
    setSortMetric,
    rowLimit,
    isLargeScreen,
    withExpandableRows,
    selectedSalesType,
    changeSortMetric,
    isStreamingPage,
    hasOneHeader,
  } = props;

  return (
    <StyledTable
      cellSpacing="0"
      cellPadding="0"
      isStreaming={isStreamingPage}
      hasOneHeader={hasOneHeader}
    >
      {isLargeScreen && (
        <TableHead
          sortMetric={sortMetric}
          sortOrder={sortOrder}
          setSortMetric={setSortMetric}
          setSortOrder={setSortOrder}
          changeSortMetric={changeSortMetric}
          headers={tableHeaders}
          withChangeMetric={
            !isStreamingPage ? withChangeMetric : withChangeMetricStreaming
          }
          hideVisually={hideVisually}
          notSortable={notSortable}
          onlyHightToLow={isStreamingPage}
          redesignedTable
          tableName={isStreamingPage ? 'StreamingTerritories' : 'Territory'}
        />
      )}
      <TableBody>
        {territories
          ? territories
              .filter((_, index) => index < rowLimit)
              .map((territory) => {
                return isStreamingPage ? (
                  isLargeScreen ? (
                    <CustomizedTableRow
                      key={`row-${territory.countryName}`}
                      tableHeaders={tableHeaders}
                      territory={territory}
                      sortMetric={sortMetric}
                      isLargeScreen={isLargeScreen}
                      isStatic={false}
                      isStreamingPage={isStreamingPage}
                    />
                  ) : (
                    <MobileTableRow
                      sortBy={sortMetric}
                      tableHeaders={tableHeaders}
                      entityData={first(territory?.data)}
                      territory={territory}
                    />
                  )
                ) : (
                  <TerritoryTableRow
                    key={`row-${territory.countryName}`}
                    tableHeaders={tableHeaders}
                    territory={territory}
                    sortMetric={sortMetric}
                    isLargeScreen={isLargeScreen}
                    isStatic={false}
                    isStreamingPage={isStreamingPage}
                  >
                    {withExpandableRows && (
                      <ExpandableTableRow
                        territory={territory}
                        selectedSalesType={selectedSalesType}
                        sortMetric={sortMetric}
                      />
                    )}
                  </TerritoryTableRow>
                );
              })
          : Array(10)
              .fill()
              .map((_, index) => (
                <TableRow key={`skeleton-loader-row-${index}`}>
                  <TableCell
                    colSpan={withExpandableRows || isStreamingPage ? 6 : 5}
                  >
                    <ContentLoader width={200} height={25}>
                      <rect fill="red" width="30" height="25" x="0" y="10" />
                      <rect fill="red" width="200" height="25" x="40" y="10" />
                    </ContentLoader>
                  </TableCell>
                </TableRow>
              ))}
      </TableBody>
    </StyledTable>
  );
}

Table.propTypes = {
  changeSortMetric: func.isRequired,
  isLargeScreen: bool.isRequired,
  rowLimit: number.isRequired,
  selectedSalesType: salesTypesPropType.isRequired,
  setSortOrder: func.isRequired,
  setSortMetric: func.isRequired,
  sortMetric: metricPropType.isRequired,
  sortOrder: oneOf(['asc', 'desc']).isRequired,
  tableHeaders: arrayOf(metricPropType).isRequired,
  territories: countriesPropType,
  withExpandableRows: bool.isRequired,
  isStreamingPage: bool,
  hasOneHeader: bool,
};

export default Table;
